import { z } from "zod";
import { type ProfessionType } from "./utils";

/**
 * The VOCABULARY of authorization: what a staff account may be allowed to do,
 * and the two shapes a principal comes in. No decisions live here — the
 * profession matrix is `capabilityMatrix.ts` and the study rules are
 * `studyAccess.ts`, both of which read this file and never the reverse.
 *
 * One vocabulary on purpose, like `ErrorCode`: the matrix maps professions
 * onto it, membership rows carry a subset, route configs name one, and the
 * `permissions` map on scoped resources is keyed by it. Adding a member is
 * deliberate friction: a Prisma enum value, a matrix decision, and at least
 * one route that requires it.
 */
export const capabilitySchema = z.enum([
  // Patient records are per-action (2026-08-13): VIEW being a real capability
  // is what lets the department pass narrow reads with a matrix edit instead
  // of a schema change. Delete rides with archive — both end a record's
  // visibility.
  "MANAGE_USERS",
  "VIEW_PATIENTS",
  "CREATE_PATIENTS",
  "EDIT_PATIENTS",
  "ARCHIVE_PATIENTS",
  "CREATE_STUDIES",
  "MANAGE_SURVEYS",
  "MANAGE_MONITORING",
  "EXPORT_DATA",
  "MANAGE_FACILITY",
  // The two logs, and two capabilities rather than one because they answer to
  // different people: the audit trail is read by a data manager or an
  // inspector reconstructing what happened to a record, the access log by a
  // DPO or incident response asking who looked at a patient. Both tables carry
  // health data, so these are the tightest tokens in the vocabulary: no
  // profession's baseline row holds either.
  "VIEW_AUDIT_LOG",
  "VIEW_ACCESS_LOG",
  // ── study-scoped: never in the matrix, never on /auth/me ─────────────────
  "VIEW_STUDY",
  "ENROLL_PATIENTS",
  "TRIGGER_SURVEYS",
  "MANAGE_STUDIES",
]);
/** Mirrors the Prisma `Capability` enum. Clients cannot import the generated one. */
export type Capability = z.infer<typeof capabilitySchema>;

/**
 * The capabilities that mean NOTHING outside one study.
 *
 * A profession cannot carry "may enrol" in the abstract: a person enrols in
 * the studies they belong to and nowhere else. So these appear in no
 * profession's row, never reach `/auth/me`, and cannot be a route's
 * `requires`. Asking the unscoped question about one is a category error, not
 * a `false` — see the throws in `capabilityMatrix.ts` and `studyAccess.ts`.
 *
 * `CREATE_STUDIES` is the unscoped half kept deliberately apart: who may OPEN
 * a study is a fact about a profession, who stewards THIS study is
 * `MANAGE_STUDIES`, a fact about a membership row. They were one token until
 * 2026-08-13 and it read correctly in exactly one direction.
 */
export type StudyScopedCapability =
  | "VIEW_STUDY"
  | "ENROLL_PATIENTS"
  | "TRIGGER_SURVEYS"
  | "MANAGE_STUDIES";

/** Everything else: what a profession can carry and `/auth/me` can answer. */
export type UnscopedCapability = Exclude<Capability, StudyScopedCapability>;

// Widened to `Capability[]` so `.includes(aCapability)` type-checks; the
// `satisfies` keeps the members honest, and a unit test keeps it exhaustive.
export const STUDY_SCOPED_CAPABILITIES: readonly Capability[] = [
  "VIEW_STUDY",
  "ENROLL_PATIENTS",
  "TRIGGER_SURVEYS",
  "MANAGE_STUDIES",
] satisfies readonly StudyScopedCapability[];

export function isUnscoped(
  capability: Capability,
): capability is UnscopedCapability {
  return !STUDY_SCOPED_CAPABILITIES.includes(capability);
}

/**
 * The three account facts an unscoped decision reads. All optional, so a
 * payload missing them (a pre-authorization JWT, an empty object) FAILS
 * CLOSED rather than failing to compile somewhere unhelpful.
 */
export type CapabilityPrincipal = {
  professionType?: ProfessionType | null;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
};

/** The principal plus the user id a membership is matched on. */
export type ScopedPrincipal = CapabilityPrincipal & { id: string };
